import { get } from 'lodash';
import { ActionContext, ActionTree } from 'vuex';
import { FeatureFlag, config, getFeatureFlag } from '@/services/config';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations, SalesChurnMutationTypes } from './mutations';
import {
  ClientApiOptions,
  ClientApiResponse,
  CollectionApiResponse,
  InteractionFormData,
  InteractionHistoryResponse,
  NextInteractionApiResponse,
  YtdKpiTableApiOptions,
  YtdKpiTableApiResponse,
} from './types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
import { formatDataValue, getDateFormatFromConfig } from '@/helpers/format';
import { LinkableClient } from '@/types/SalesChurn';
import { format } from '@/helpers/format'

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export enum SalesChurnActionTypes {
  SET_CLIENT_LOADING = 'SET_CLIENT_LOADING',
  GET_CLIENT = 'GET_CLIENT',
  GET_GROUP = 'GET_GROUP',
  GET_YTD_KPI_DATA = 'GET_YTD_KPI_DATA',
  GET_COLLECTIONS = 'GET_COLLECTIONS',
  RESET_SALES_CHURN_STATE = 'RESET_SALES_CHURN_STATE',
  ADD_PAST_INTERACTION = 'ADD_PAST_INTERACTION',
  ADD_FUTURE_INTERACTION = 'ADD_FUTURE_INTERACTION',
  GET_NEXT_INTERACTION = 'GET_NEXT_INTERACTION',
  SET_INTERACTION_ERROR = 'SET_INTERACTION_ERROR',
  GET_LINKABLE_CLIENTS = 'GET_LINKABLE_CLIENTS',
  RESET_LINKABLE_CLIENTS = 'RESET_LINKABLE_CLIENTS',
  LINK_CLIENTS = 'LINK_CLIENTS',
  UNLINK_CLIENTS = 'UNLINK_CLIENTS',
  GET_INERACTION_HISTORY = 'GET_INERACTION_HISTORY',
  RESET_INTERACTION_HISTORY = 'RESET_INTERACTION_HISTORY',
  CHANGE_PERSONA = 'CHANGE_PERSONA',
  TOGGLE_OPPORTUNITY_OPEN = 'TOGGLE_OPPORTUNITY_OPEN',
}

export type Actions = {
  [SalesChurnActionTypes.SET_CLIENT_LOADING](
    { commit }: AugmentedAction,
    payload: boolean
  ): Promise<void>;
  [SalesChurnActionTypes.GET_CLIENT](
    { commit }: AugmentedAction,
    payload: ClientApiOptions
  ): Promise<void>;
  [SalesChurnActionTypes.GET_GROUP](
    { commit }: AugmentedAction,
    payload: ClientApiOptions
  ): Promise<void>;
  [SalesChurnActionTypes.GET_YTD_KPI_DATA](
    { commit }: AugmentedAction,
    filterOptions: YtdKpiTableApiOptions
  ): Promise<{ collectionIds: string[]; containerIds: string[] }>;
  [SalesChurnActionTypes.GET_COLLECTIONS](
    { commit }: AugmentedAction,
    clientId: string
  ): Promise<void>;
  [SalesChurnActionTypes.RESET_SALES_CHURN_STATE]({
    commit,
  }: AugmentedAction): Promise<void>;
  [SalesChurnActionTypes.ADD_PAST_INTERACTION](
    { commit }: AugmentedAction,
    payload: {
      clientId: string;
      formData: InteractionFormData;
    }
  ): Promise<void>;
  [SalesChurnActionTypes.ADD_FUTURE_INTERACTION](
    { commit }: AugmentedAction,
    payload: {
      clientId: string;
      formData: InteractionFormData;
    }
  ): Promise<void>;
  [SalesChurnActionTypes.GET_NEXT_INTERACTION](
    { commit }: AugmentedAction,
    payload: {
      clientId: string;
    }
  ): Promise<void>;
  [SalesChurnActionTypes.SET_INTERACTION_ERROR](
    { commit }: AugmentedAction,
    payload: boolean
  ): Promise<void>;
  [SalesChurnActionTypes.GET_LINKABLE_CLIENTS](
    { commit }: AugmentedAction,
    clientId: string
  ): Promise<void>;
  [SalesChurnActionTypes.RESET_LINKABLE_CLIENTS]({
    commit,
  }: AugmentedAction): Promise<void>;
  [SalesChurnActionTypes.LINK_CLIENTS](
    { commit }: AugmentedAction,
    payload: {
      clientsToLink: Array<string>;
      mainClientId: string;
      groupsToRemove: Array<string>;
    }
  ): Promise<boolean>;
  [SalesChurnActionTypes.UNLINK_CLIENTS](
    { commit }: AugmentedAction,
    payload: {
      clientsToUnlink: Array<string>;
      mainClientGroupToTarget: string;
      deleteGroup: boolean;
    }
  ): Promise<boolean>;
  [SalesChurnActionTypes.GET_INERACTION_HISTORY](
    { commit }: AugmentedAction,
    payload: {
      clientId: string;
    }
  ): Promise<void>;
  [SalesChurnActionTypes.RESET_INTERACTION_HISTORY]({
    commit,
  }: AugmentedAction): Promise<void>;
  [SalesChurnActionTypes.CHANGE_PERSONA](
    { commit }: AugmentedAction,
    payload: { clientId: string; persona: string }
  ): Promise<void>;
  [SalesChurnActionTypes.TOGGLE_OPPORTUNITY_OPEN](
    { commit }: AugmentedAction,
    taskId: string
  ): void;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [SalesChurnActionTypes.SET_CLIENT_LOADING]({ commit }, payload) {
    commit(SalesChurnMutationTypes.SET_CLIENT_LOADING, payload);
  },
  async [SalesChurnActionTypes.GET_CLIENT](
    { commit },
    payload: ClientApiOptions
  ) {
    try {
      const clientData: ClientApiResponse = await ServerAPI.get(
        `/clients-new/${payload.clientId}`
      );

      commit(SalesChurnMutationTypes.SET_CLIENT, clientData);
    } catch (e) {
      handleError(e);
      commit(SalesChurnMutationTypes.SET_CLIENT, {} as ClientApiResponse);
    }
  },
  async [SalesChurnActionTypes.GET_GROUP](
    { commit },
    payload: ClientApiOptions
  ) {
    try {
      const clientData: ClientApiResponse = await ServerAPI.get(
        `/groups/${payload.clientId}`
      );

      commit(SalesChurnMutationTypes.SET_GROUP, clientData);
    } catch (e) {
      handleError(e);
      commit(SalesChurnMutationTypes.SET_GROUP, null);
    }
  },
  async [SalesChurnActionTypes.GET_YTD_KPI_DATA](
    { commit },
    options: YtdKpiTableApiOptions
  ) {
    try {
      commit(SalesChurnMutationTypes.SET_YTD_KPI_DATA_LOADING, true);

      const sortBy = get(
        config.layout,
        'clientView.ytdKpiTable.options.sortBy',
        'aum'
      );

      const paramString = combineQueryParams({
        ['filters']: [
          { key: 'client_id', value: options.clientId },
          { key: 'banker_id', value: options.bankerId },
        ],
        ['sort_by']: sortBy,
        ['sort_descending']: true,
        ['size']: 999999,
        ['start']: 0,
      });

      const ytdKpiTableData: YtdKpiTableApiResponse = await ServerAPI.get(
        `/portfolios${paramString}`
      );

      commit(SalesChurnMutationTypes.SET_YTD_KPI_DATA, ytdKpiTableData.results);

      return {
        containerIds: ytdKpiTableData.results
          ? ytdKpiTableData.results.map(
              item => item.portfolioId.dataValue as string
            )
          : [],
        collectionIds: ytdKpiTableData.results
          ? ytdKpiTableData.results.map(
              item => item.familyId.dataValue as string
            )
          : [],
      };
    } catch (e) {
      handleError(e);
      return {
        containerIds: [],
        collectionIds: [],
      };
    } finally {
      commit(SalesChurnMutationTypes.SET_YTD_KPI_DATA_LOADING, false);
    }
  },
  async [SalesChurnActionTypes.GET_COLLECTIONS]({ commit }, clientId) {
    commit(SalesChurnMutationTypes.SET_COLLECTIONS_LOADING, true);
    try {
      const linkClientsEnabled = getFeatureFlag(FeatureFlag.LINK_CLIENTS)
        .enabled;

      const path = linkClientsEnabled ? 'groups' : 'clients-new';

      const collectionData: CollectionApiResponse[] = await ServerAPI.get(
        `/${path}/${clientId}/collections`
      );

      commit(SalesChurnMutationTypes.SET_COLLECTIONS, collectionData);
    } catch (e) {
      handleError(e);
    } finally {
      commit(SalesChurnMutationTypes.SET_COLLECTIONS_LOADING, false);
    }
  },
  async [SalesChurnActionTypes.RESET_SALES_CHURN_STATE]({ commit }) {
    commit(SalesChurnMutationTypes.RESET_SALES_CHURN_STATE, undefined);
  },
  async [SalesChurnActionTypes.ADD_PAST_INTERACTION]({ commit }, payload) {
    try {
      if (payload.formData.interactionDate) {
        payload.formData.interactionDate = format(
          new Date(payload.formData.interactionDate),
          getDateFormatFromConfig().dnsFormat
        );
      }

      await ServerAPI.post(
        `/clients-new/${payload.clientId}/interactions`,
        payload.formData
      );
    } catch (e) {
      commit(SalesChurnMutationTypes.SET_INTERACTION_ERROR, true);
      handleError(e);
    }
  },
  async [SalesChurnActionTypes.ADD_FUTURE_INTERACTION]({ commit }, payload) {
    try {
      if (payload.formData.interactionDate) {
        payload.formData.interactionDate = format(
          new Date(payload.formData.interactionDate),
          getDateFormatFromConfig().dnsFormat
        );
      }

      await ServerAPI.post(
        `/clients-new/${payload.clientId}/interactions/next`,
        payload.formData
      );
    } catch (e) {
      commit(SalesChurnMutationTypes.SET_INTERACTION_ERROR, true);
      handleError(e);
    }
  },
  async [SalesChurnActionTypes.GET_NEXT_INTERACTION]({ commit }, payload) {
    try {
      const res: NextInteractionApiResponse = await ServerAPI.get(
        `/clients-new/${payload.clientId}/interactions/next`
      );

      commit(SalesChurnMutationTypes.SET_CLIENT_NEXT_INTERACTION, {
        userId: formatDataValue(res.userId) as string,
        date: formatDataValue(res.nextInteraction, {
          overrideDateFormat: true,
        }) as string,
        dateString: res.nextInteraction.dataValue as string,
        startTime: formatDataValue(res.startTime) as string,
        endTime: formatDataValue(res.endTime) as string,
        overdue: formatDataValue(res.overdue) as boolean,
        type: formatDataValue(res.interactionType) as string,
        actions: res.actionsDiscussed.map(action => action.dataValue as string),
      });
    } catch (e) {
      handleError(e);
    }
  },
  async [SalesChurnActionTypes.SET_INTERACTION_ERROR]({ commit }, payload) {
    commit(SalesChurnMutationTypes.SET_INTERACTION_ERROR, payload);
  },
  async [SalesChurnActionTypes.GET_LINKABLE_CLIENTS]({ commit }, clientId) {
    commit(SalesChurnMutationTypes.SET_LINK_MODAL_LOADING, true);

    try {
      const res: LinkableClient[] = await ServerAPI.get(
        `/clients-new/${clientId}/clients-to-link`
      );

      const linkableClients = res.filter(
        item => item.client.clientId.dataValue !== clientId
      );

      commit(SalesChurnMutationTypes.SET_LINKABLE_CLIENTS, linkableClients);
    } catch (e) {
      handleError(e);
    } finally {
      commit(SalesChurnMutationTypes.SET_LINK_MODAL_LOADING, false);
    }
  },
  async [SalesChurnActionTypes.RESET_LINKABLE_CLIENTS]({ commit }) {
    commit(SalesChurnMutationTypes.RESET_LINKABLE_CLIENTS, undefined);
  },
  async [SalesChurnActionTypes.LINK_CLIENTS]({ commit }, payload) {
    commit(SalesChurnMutationTypes.SET_LINK_MODAL_SUBMITTING, true);
    try {
      const path = '/clients-new/link';
      await ServerAPI.post(path, {
        clientsToLink: payload.clientsToLink,
        mainClientId: payload.mainClientId,
        groupsToRemove: payload.groupsToRemove,
      });
      return true;
    } catch (e) {
      handleError(e);
      return false;
    } finally {
      commit(SalesChurnMutationTypes.SET_LINK_MODAL_SUBMITTING, false);
    }
  },
  async [SalesChurnActionTypes.UNLINK_CLIENTS]({ commit }, payload) {
    commit(SalesChurnMutationTypes.SET_LINK_MODAL_SUBMITTING, true);
    try {
      const path = '/clients-new/unlink';
      await ServerAPI.post(path, {
        clientsToUnlink: payload.clientsToUnlink,
        mainClientGroupToTarget: payload.mainClientGroupToTarget,
        deleteGroup: payload.deleteGroup,
      });
      return true;
    } catch (e) {
      handleError(e);
      return false;
    } finally {
      commit(SalesChurnMutationTypes.SET_LINK_MODAL_SUBMITTING, false);
    }
  },
  async [SalesChurnActionTypes.GET_INERACTION_HISTORY]({ commit }, payload) {
    commit(SalesChurnMutationTypes.SET_INTERACTION_HISTORY_LOADING, true);
    try {
      const path = `/clients-new/${payload.clientId}/interactions/history`;
      const res: InteractionHistoryResponse[] = await ServerAPI.get(path);
      commit(SalesChurnMutationTypes.SET_INTERACTION_HISTORY, res);
    } catch (e) {
      handleError(e);
      commit(SalesChurnMutationTypes.RESET_INTERACTION_HISTORY, undefined);
    } finally {
      commit(SalesChurnMutationTypes.SET_INTERACTION_HISTORY_LOADING, false);
    }
  },
  async [SalesChurnActionTypes.RESET_INTERACTION_HISTORY]({ commit }) {
    commit(SalesChurnMutationTypes.RESET_INTERACTION_HISTORY, undefined);
  },
  async [SalesChurnActionTypes.CHANGE_PERSONA](_, payload) {
    try {
      await ServerAPI.patch(`/clients-new/${payload.clientId}/persona`, {
        persona: payload.persona,
      });
    } catch (e) {
      handleError(e);
    }
  },
  [SalesChurnActionTypes.TOGGLE_OPPORTUNITY_OPEN](
    { commit }: AugmentedAction,
    taskId: string
  ): void {
    commit(SalesChurnMutationTypes.TOGGLE_OPPORTUNITY_OPEN, taskId);
  },
};
